/**
 * Stripe Subscription Page Component
 * 
 * Main container that handles URL query parameters and switches between
 * ProductDisplay, SuccessDisplay, and Message views based on the checkout result.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../../i18n/hooks';
import { useAuth } from '../../auth/hooks';
import { PageContainer, ArbiWinHeader, ArbiWinFooter, MainContainer } from '../../ui';
import ProductDisplay from './ProductDisplay';
import SuccessDisplay from './SuccessDisplay';
import { trackEvent } from '../../analytics/services/analyticsService';
import '../../public/components/StaticPages.css';
import './StripeSubscriptionPage.css';
import '../styles/StripeStyles.css';

/**
 * Message Component - Displays error or informational messages
 * @param {Object} props - Component props
 * @param {string} props.message - Message to display
 * @returns {JSX.Element} Message component
 */
const MessageDisplay = ({ message }) => {
  return (
    <section className="stripe-message-display">
      <p>{message}</p>
    </section>
  );
};

/**
 * StripeSubscriptionPage - Main subscription page container
 * @returns {JSX.Element} Subscription page component
 */
const StripeSubscriptionPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);
  const [sessionId, setSessionId] = useState('');

  useEffect(() => {
    const pathname = location.pathname;
    const query = new URLSearchParams(location.search);
    const isSuccessPath = pathname === '/subscription/success' || query.get('success') === 'true';
    const isCancelPath = pathname === '/subscription/cancel' || query.get('canceled') === 'true';

    if (isSuccessPath) {
      setSuccess(true);
      const sessionIdParam = query.get('session_id');
      if (sessionIdParam) {
        setSessionId(sessionIdParam);

        const hasTracked = localStorage.getItem(`tracked_purchase_${sessionIdParam}`);
        if (!hasTracked) {
          trackEvent('purchase', { value: 35.00, currency: 'BGN', transaction_id: sessionIdParam });
          localStorage.setItem(`tracked_purchase_${sessionIdParam}`, 'true');
        }
      }
      setMessage('');
    } else if (isCancelPath) {
      setSuccess(false);
      setMessage(
        t('stripe.messages.canceled') ||
        "Order canceled -- continue to shop around and checkout when you're ready."
      );
      setSessionId('');
    } else {
      setSuccess(false);
      setMessage('');
      setSessionId('');
    }
  }, [location.pathname, location.search, t]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/');
    }
  };

  // Render based on state
  return (
    <PageContainer>
      <ArbiWinHeader
        isAuthenticated={isAuthenticated}
        user={user}
        onLogin={() => navigate('/login')}
        onRegister={() => navigate('/register')}
        onLogout={handleLogout}
        onProfile={() => navigate('/profile')}
        onHomeClick={() => navigate('/')}
      />
      <MainContainer className="stripe-page-main-container">
        <div className="stripe-content-wrapper">
          {!success && message === '' && <ProductDisplay />}
          {success && sessionId !== '' && <SuccessDisplay sessionId={sessionId} />}
          {message !== '' && <MessageDisplay message={message} />}
        </div>
      </MainContainer>
      <ArbiWinFooter />
    </PageContainer>
  );
};

export default StripeSubscriptionPage;

